import React, { useEffect } from "react";
import Cal, { getCalApi } from "@calcom/embed-react";
import { trackConversion } from "../../lib/analytics";

/**
 * Cal.com inline booking widget for the diagnostic result screen.
 *
 * Uses Cal.com's official React SDK so the embed script loads reliably.
 * The widget is themed to match the HuddleUp brand and fires a
 * conversion event (GA4 + LinkedIn) when a booking is confirmed.
 *
 * Props:
 *   calLink  — Cal.com path, e.g. "huddleup/30min" (no host, no leading slash)
 *   product  — recommended product from the diagnostic, forwarded to the
 *              conversion event as an attribution label
 *   notes    — optional text prefilled into the booking's "notes" field so
 *              the diagnostic answers travel with the meeting
 *   onBooked — optional callback fired on bookingSuccessful with a
 *              normalised { start, duration, title, name, email, timeZone }
 */
const NAMESPACE = "huddleup-30min";

export default function CalEmbed({ calLink = "huddleup/30min", product, notes, onBooked }) {
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cal = await getCalApi({ namespace: NAMESPACE });
        if (!mounted) return;

        cal("ui", {
          theme: "dark",
          cssVarsPerTheme: {
            light: { "cal-brand": "#C9A961" },
            dark: { "cal-brand": "#C9A961" },
          },
          hideEventTypeDetails: false,
          layout: "month_view",
        });

        cal("on", {
          action: "bookingSuccessful",
          callback: (e) => {
            trackConversion("call-booked", product);
            const d = e?.detail?.data || {};
            const attendee = d.booking?.attendees?.[0] || {};
            onBooked?.({
              start: d.date || d.booking?.startTime || null,
              duration: d.duration || d.eventType?.length || 30,
              title: d.eventType?.title || d.booking?.title || "HuddleUp Intro Call",
              name: attendee.name || "",
              email: attendee.email || "",
              timeZone: attendee.timeZone || "",
            });
          },
        });
      } catch {
        /* Cal.com embed script blocked — the fallback link is still shown. */
      }
    })();
    return () => {
      mounted = false;
    };
  }, [product, onBooked]);

  return (
    <div data-testid="cal-inline-embed" className="w-full">
      <Cal
        namespace={NAMESPACE}
        calLink={calLink}
        style={{ width: "100%", height: "680px", overflow: "auto" }}
        config={{ layout: "month_view", theme: "dark", ...(notes ? { notes } : {}) }}
      />
    </div>
  );
}
